import React from "react";
import {
  AlertCircle,
  Info,
  Search,
  Plus,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import type {
  CommerceHelpCalloutViewModel,
  ManagementWorkspaceShellViewModel,
} from "../../contracts/commerce-admin";
import {
  AdminShell,
  WorksurfaceHeader,
  HorizontalStack,
  VerticalStack,
  DescriptionText,
} from "../blocks";

const calloutStyles: Record<string, string> = {
  info:    "border-blue-500/30 bg-blue-500/5 text-blue-700 dark:text-blue-300",
  success: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
  warning: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300",
  danger:  "border-destructive/30 bg-destructive/5 text-destructive",
};

const calloutIconMap: Record<string, React.ReactNode> = {
  info:    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />,
  warning: <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />,
  danger:  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />,
  success: null,
};

function InlineCallout({ callout }: { callout: CommerceHelpCalloutViewModel }) {
  return (
    <div className={cn(
      "flex items-start gap-2.5 rounded-lg border px-4 py-3 text-xs",
      calloutStyles[callout.tone] ?? calloutStyles.info
    )}>
      {calloutIconMap[callout.tone]}
      <div>
        <p className="font-black uppercase tracking-tight mb-0.5">{callout.title}</p>
        <p className="opacity-80 leading-relaxed font-medium">{callout.body}</p>
      </div>
    </div>
  );
}

export interface ManagementWorkspaceShellProps {
  viewModel: ManagementWorkspaceShellViewModel;
  children?: React.ReactNode;
  className?: string;
  contentAreaClassName?: string;
  /** When false, removes the default inset so full-width tables keep the scrollbar flush with the workspace edge. */
  padded?: boolean;
  titleContent?: React.ReactNode;
  registryContent?: React.ReactNode;
  /**
   * Dedicated takeover surface for detail, review, and editor flows.
   * Do not render the takeover UI as a sibling beside the registry surface.
   * Pass it here and toggle `takeoverActive` so the shell owns layout replacement.
   */
  takeoverContent?: React.ReactNode;
  /**
   * When true and `takeoverContent` is present, the registry/list surface is replaced by the takeover surface.
   */
  takeoverActive?: boolean;
  /**
   * When true, the standard shell header is hidden while the takeover surface is active.
   */
  replaceShellHeaderWhenTakeoverActive?: boolean;
}

export function ManagementWorkspaceShell({
  viewModel,
  children,
  className,
  contentAreaClassName,
  padded = true,
  titleContent,
  registryContent,
  takeoverContent,
  takeoverActive = false,
  replaceShellHeaderWhenTakeoverActive = false,
}: ManagementWorkspaceShellProps) {
  const {
    ownerTenant,
    access,
    toolbar,
    callouts,
  } = viewModel;
  const mainContent = registryContent ?? children;
  const showTakeover = Boolean(takeoverActive && access.accessEnabled && takeoverContent);
  const hideShellHeader = showTakeover && replaceShellHeaderWhenTakeoverActive;
  const showSearch = toolbar.showSearch !== false;

  const runToolbarAction = (href?: string | null, onAction?: () => void) => {
    if (onAction) { onAction(); return; }
    if (href) {
      window.history.pushState({}, "", href);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  return (
    <AdminShell
      title={titleContent || (
        <WorksurfaceHeader
          parentLabel="Commerce Management"
          activeLabel={ownerTenant.tenantLabel}
          description="Manage reseller publications, pricebooks, and targeted assignments."
        />
      )}
      actions={hideShellHeader ? null : (
        <HorizontalStack gap={2} className="items-center w-full sm:w-auto sm:gap-3">
          {showSearch ? (
            <div className="relative flex-1 sm:flex-none">
              <>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60 pointer-events-none" />
                <input
                  type="text"
                  aria-label="Search commerce workspace"
                  value={toolbar.searchValue}
                  onChange={e => toolbar.onSearchChange(e.target.value)}
                  placeholder={toolbar.searchPlaceholder}
                  className="pl-9 pr-8 py-1.5 bg-muted/40 border border-border/40 rounded-md text-xs w-full sm:w-48 sm:focus:w-64 focus:bg-background focus:border-primary/40 transition-all outline-none"
                />
                {toolbar.searchValue && (
                  <button
                    onClick={() => toolbar.onSearchChange("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </>
            </div>
          ) : null}

          {toolbar.secondaryAction ? (
            <button
              onClick={() => runToolbarAction(toolbar.secondaryAction?.href, toolbar.secondaryAction?.onAction)}
              disabled={toolbar.secondaryAction.disabled || (!toolbar.secondaryAction.onAction && !toolbar.secondaryAction.href)}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-border/50 text-foreground rounded-md text-xs font-bold hover:bg-muted/30 disabled:opacity-50 transition-all shadow-sm"
            >
              {toolbar.secondaryAction.label}
            </button>
          ) : null}

          {toolbar.primaryAction ? (
            <button
              onClick={() => toolbar.primaryAction?.onAction?.()}
              disabled={toolbar.primaryAction.disabled || !toolbar.primaryAction.onAction}
              className="flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 sm:px-3 bg-primary text-primary-foreground rounded-md text-xs font-bold hover:opacity-90 disabled:opacity-50 transition-all shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{toolbar.primaryAction.label}</span>
            </button>
          ) : null}
        </HorizontalStack>
      )}
      className={cn("bg-background", className)}
      bodyClassName="flex min-h-0 flex-col"
      headerClassName={hideShellHeader ? "hidden" : undefined}
      flat
    >
      {showTakeover && hideShellHeader ? (
        <div className="relative flex h-full min-h-0 flex-1 flex-col">
          {takeoverContent}
        </div>
      ) : (
        <>
      <div className={cn("relative flex-1 flex flex-col min-h-0", contentAreaClassName)}>
        {!showTakeover && callouts.length > 0 && (
          <div className={cn("flex flex-col gap-2 lg:hidden mb-4", padded && "px-3 pt-3 sm:px-6 sm:pt-6")}>
            {callouts.map(callout => (
              <InlineCallout key={callout.id} callout={callout} />
            ))}
          </div>
        )}

        <div className={cn("flex-1 flex gap-8 min-h-0", showTakeover && "hidden")} aria-hidden={showTakeover}>
          <div className={cn("flex-1 min-w-0", padded ? "overflow-y-auto" : "flex flex-col min-h-0")}>
            {!access.accessEnabled ? (
              <VerticalStack gap={4} className="h-64 items-center justify-center text-center opacity-60">
                <AlertCircle className="w-8 h-8 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-bold">Access Restricted</p>
                  <DescriptionText className="max-w-xs">
                    {access.blockingReason ?? "You do not have the necessary permissions to manage this workspace."}
                  </DescriptionText>
                </div>
              </VerticalStack>
            ) : padded ? (
              <div className="p-3 sm:p-6">
                {mainContent}
              </div>
            ) : (
              mainContent
            )}
          </div>

          {callouts.length > 0 && (
            <aside className={cn("hidden lg:flex w-72 flex-col gap-8 flex-shrink-0 animate-in fade-in slide-in-from-right-4 duration-500", padded && "pt-3 sm:pt-6 pr-3 sm:pr-6")}>
              <VerticalStack gap={3}>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 px-1">
                  Guidance & Alerts
                </p>
                <VerticalStack gap={3}>
                  {callouts.map(callout => (
                    <InlineCallout key={callout.id} callout={callout} />
                  ))}
                </VerticalStack>
              </VerticalStack>
            </aside>
          )}
        </div>

        {showTakeover ? (
          <div className="absolute inset-0 z-20 min-h-0">
            {takeoverContent}
          </div>
        ) : null}
      </div>
        </>
      )}
    </AdminShell>
  );
}
